"use client";
import { useCallback, useEffect, useState } from "react";
import { useScopedI18n } from "@/messages/client";
import Title from "@/components/ui/title";
import InfiniteMoving from "@/components/ui/infinite-moving";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { DirectusImage } from "@/components/DirectusImage";
import RouterLink from "@/components/ui/router-link";
import { ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import "./index.css";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import useEmblaCarousel from "embla-carousel-react";

export default function ProductOverviewClient(props: {
  data?: {
    slug?: string;
    name?: string;
    feature?: string;
    advantage_home_image?: string;
    advantage_image?: string;
    advantage_data?: {
      description?: string;
      icon?: string;
    }[];
    recommend_product?: {
      slug?: string;
      name?: string;
      card_cover?: string;
    }[];
  }[];
}) {
  const t = useScopedI18n("产品");
  const tCommon = useScopedI18n("通用");

  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);

  const [thumbsRef, thumbApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    loop: true,

    breakpoints: {
      "(min-width: 768px)": {
        loop: true,
        dragFree: true,
        containScroll: "keepSnaps",
      },
    },
  });

  const onSelect = useCallback(() => {
    if (!mainApi || !thumbApi) {
      return;
    }

    setCurrent(mainApi.selectedScrollSnap() || 0);
    thumbApi?.scrollTo(mainApi.selectedScrollSnap() || 0);
  }, [mainApi, thumbApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  return (
    <div className="w-full max-md:-ml-6 max-md:w-[100vw] max-md:bg-white max-md:px-6">
      <Title title={t("选择适合您的产品")} />
      <div className="md:hidden">
        <Accordion
          type="single"
          collapsible
          className="mt-4 flex flex-col gap-y-2">
          {props.data?.map((el, index) => (
            <AccordionItem
              className="border-none"
              key={index}
              value={`category_${index}`}>
              <AccordionTrigger className="flex items-center justify-between rounded-lg bg-zinc-50 p-4 text-sm">
                {el.name}
              </AccordionTrigger>
              <AccordionContent className="bg-zinc-50 px-3 pb-5 pt-1">
                <div className="rounded-xl bg-white px-4 pb-4 pt-5">
                  <h4 className="text-lg">{t("产品特性")}</h4>
                  <p className="mt-1.5 whitespace-pre-line text-sm">
                    {el.feature}
                  </p>
                  <h4 className="mt-6 text-lg">{t("产品优势")}</h4>
                  <div className="mt-3 grid grid-cols-3 gap-x-3">
                    {el.advantage_data?.map((item, i) => (
                      <div
                        className={cn("flex justify-center", {
                          "justify-start": i === 0,
                          "justify-end":
                            i === (el.advantage_data?.length || 1) - 1,
                        })}
                        key={`advantage_${index}_${i}`}>
                        <div
                          className="flex flex-col items-center"
                          key={`advantage_${index}_${i}`}>
                          <DirectusImage
                            id={item.icon || ""}
                            alt={`${t("产品优势")} ${i + 1}`}
                            width={40}
                            height={40}
                            className="h-[40px] w-auto"
                          />
                          <div className="mt-2 whitespace-pre-line text-center text-xs">
                            {item.description
                              ?.split("\n")
                              ?.map((line, index) => (
                                <div key={index}>{line}</div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 w-full">
                    <DirectusImage
                      id={el.advantage_home_image || el.advantage_image || ""}
                      alt={el.name || ""}
                      width={263}
                      height={148}
                      className="h-[40vw] w-full rounded-md object-cover"
                    />
                  </div>
                </div>
                <div className="flex w-full gap-x-4 overflow-x-auto px-4 pb-2 pt-6">
                  {el.recommend_product?.map((item, i) => (
                    <RouterLink
                      key={`category_${index}_product_${i}`}
                      href={el.slug ? `/product/${el.slug}` : undefined}
                      className="w-[200px] shrink-0">
                      <div className="h-[112px] w-full rounded-t-lg shadow-[0_-3px_2px_1px_rgba(0,0,0,0.05)]">
                        <DirectusImage
                          id={item.card_cover || ""}
                          alt={item.name || ""}
                          width={200}
                          height={112}
                          className="h-full w-full rounded-t-lg object-cover"
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-between px-2">
                        <p className="text-sm font-bold">{item.name}</p>
                        <p className="text-xs text-zinc-400">
                          {tCommon("前往了解详情")}
                          <ChevronsRight className="ml-1 inline" size={14} />
                        </p>
                      </div>
                    </RouterLink>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <div className="hidden md:block">
        <div className="relative w-full px-10 xl:px-16">
          <div className="mt-7 w-full">
            <div className="relative overflow-hidden" ref={thumbsRef}>
              <div className="flex">
                {props.data?.map((el, index) => (
                  <div key={index} className="px-10 pb-1">
                    <div
                      className={cn("category-tab whitespace-nowrap py-3", {
                        "category-tab-active": index === current,
                      })}
                      onClick={() => {
                        mainApi?.scrollTo(index);
                      }}>
                      {el.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <span
            className="category-prev xl:l-20"
            onClick={() => {
              mainApi?.scrollPrev();
            }}>
            <ChevronLeft />
          </span>
          <span
            className="category-next xl:r-20"
            onClick={() => {
              mainApi?.scrollNext();
            }}>
            <ChevronRight />
          </span>
        </div>
        <Carousel
          className="mt-7 w-full"
          opts={{ loop: true, duration: 0, watchDrag: false }}
          setApi={setMainApi}>
          <CarouselContent className="m-0">
            {props.data?.map((el, index) => (
              <CarouselItem className="basis-full p-0" key={index}>
                <div className="flex h-[500px] overflow-hidden rounded-xl bg-white xl:h-[560px]">
                  <div className="w-[53%] min-w-[450px] pl-24 pr-10 pt-11">
                    <h3 className="text-3xl font-bold">{el.name}</h3>
                    <h4 className="mt-12 text-xl">{t("产品特性")}</h4>
                    <p className="mt-5 line-clamp-4 h-24 text-ellipsis whitespace-pre-line text-base xl:line-clamp-6 xl:h-36">
                      {el.feature}
                    </p>
                    <h4 className="mt-4 text-xl">{t("产品优势")}</h4>
                    <div className="grid grid-cols-3 gap-x-6">
                      {el.advantage_data?.map((item, i) => (
                        <div
                          className={cn("flex justify-center", {
                            "justify-start": i === 0,
                            "justify-end":
                              i === (el.advantage_data?.length || 1) - 1,
                          })}
                          key={`advantage_${index}_${i}`}>
                          <div className="mt-5 flex flex-col items-center pr-2">
                            <DirectusImage
                              id={item.icon || ""}
                              alt={`${t("产品优势")} ${i + 1}`}
                              width={70}
                              height={70}
                              className="h-[70px] w-auto"
                            />
                            <div className="mt-2 line-clamp-3 text-ellipsis whitespace-pre-line text-center text-xs">
                              {item.description
                                ?.split("\n")
                                ?.map((line, index) => (
                                  <div key={index}>{line}</div>
                                ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex grow basis-0 items-center justify-center overflow-hidden">
                    <DirectusImage
                      id={el.advantage_home_image || el.advantage_image || ""}
                      alt={el.name || ""}
                      width={660}
                      height={600}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="mt-12 flex items-center justify-between">
          <h4 className="ml-24 text-3xl">{t("主推产品")}</h4>
          <RouterLink
            href={`/product-category/${props.data?.[current].slug}`}
            className="text-sm text-gray">
            {t("全部主推产品")}
            <ChevronsRight className="inline" size={16} />
          </RouterLink>
        </div>
        <InfiniteMoving
          className="relative left-[50%] mt-8 w-[calc(100vw-20px)] translate-x-[-50%] py-2"
          speed="slow"
          gap="xl"
          disabled={(props.data?.[current]?.recommend_product?.length || 0) < 3}
          key={current}>
          {props.data?.[current]?.recommend_product?.map((el, index) => (
            <RouterLink
              key={`category_${current}_product_${index}`}
              className="group relative h-[222px] w-[334px]"
              href={el.slug ? `/product/${el.slug}` : undefined}>
              <div className="outline-shadow flex items-center justify-center rounded-t-lg group-hover:rounded-lg group-hover:bg-white group-hover:bg-opacity-80">
                {/* 模糊+透明蒙层单独一层，避免影响阴影效果 */}
                <div className="flex h-[188px] w-full justify-center overflow-hidden rounded-t-lg bg-[url(/product-bg-2.jpeg)] bg-cover duration-300 group-hover:h-[222px] group-hover:rounded-lg group-hover:blur-[4px]">
                  <DirectusImage
                    id={el.card_cover || ""}
                    defaultImage="/default-bg.webp"
                    alt={el.name || ""}
                    width={220}
                    height={124}
                    className="h-full object-scale-down duration-300 group-hover:scale-[1.5] group-hover:opacity-80"
                  />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 flex h-[35px] items-center justify-between bg-[#f9f9f9] px-6 duration-300 group-hover:bottom-6 group-hover:bg-transparent">
                <img
                  className="absolute -top-12 left-6 z-10 w-9 opacity-0 duration-300 group-hover:opacity-100"
                  src="/icon/analytics.svg"
                  alt="Recommend Product"
                />
                <p className="text-lg font-bold text-default duration-300 group-hover:text-xl">
                  {el.name}
                </p>
                <p className="text-xs text-zinc-400">
                  {tCommon("前往了解详情")}
                  <ChevronsRight className="inline" size={16} />
                </p>
              </div>
            </RouterLink>
          ))}
        </InfiniteMoving>
      </div>
    </div>
  );
}
